// C:\MonProjet\BBW_DELEGUER\preprod\babyworld-backoffice\babyworld-backoffice\src\pages\analytics\ReturnIndexPage\index.js

import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { 
    Card, Select, DatePicker, Button, Table, Typography, Spin, message, 
    Row, Col, Tooltip, Statistic, Modal as AntdModal, List, Empty, Tag, Space,
    Collapse, Badge 
} from 'antd';
import { 
    SearchOutlined, InfoCircleOutlined, ArrowUpOutlined, ArrowDownOutlined, 
    CheckCircleOutlined, IssuesCloseOutlined, TeamOutlined, BarChartOutlined, 
    FilterOutlined, ScheduleOutlined, DownloadOutlined, AuditOutlined, 
    UserOutlined, EnvironmentOutlined, RocketOutlined, StarFilled, CrownOutlined,
    WarningOutlined, StopOutlined
} from '@ant-design/icons';
import dayjs from 'dayjs';
import { useSelector, useDispatch } from 'react-redux';
import { getAllDelegatesList } from '../../../services/delegateService'; 
import { getPhysicianReturnIndexForDelegate, exportPhysicianReturnIndex } from '../../../services/analyticsService'; 
import { fetchCycles } from '../../../redux/slices/planning/planningThunks';
import { getDelegatesList } from '../../../redux/slices/delegates/delegatesThunk'; 
import { returnIndexTableColumns } from './columns';
import { delegatePerformanceTableColumns } from './delegatePerformanceColumns';
import axiosInstance from "../../../services/axiosServicesVisit";
import DoctorHistoryModal from '../../../components/DoctorHistoryModal';
import Papa from 'papaparse';

const { RangePicker } = DatePicker;
const { Title, Text } = Typography;
const { Option } = Select;

// =========================================================================
// ⚡ CONFIGURATION DE LA SEGMENTATION RELATIONNELLE (IDENTIQUE À RAPPORTS) ⚡
// =========================================================================
export const RELATIONSHIP_CONFIG = {
  PARTNER_ACTIVE: {
    label: "⭐ Partenaire Stratégique",
    tagColor: "gold",
    icon: <StarFilled />,
    bgColor: "#fffbe6",
    borderColor: "#fadb14",
    titleColor: "#856404",
    shadow: "0 4px 12px rgba(250, 219, 20, 0.18)"
  },
  PARTNER_AT_RISK: {
    label: "⚠️ Partenaire Sous Vigilance",
    tagColor: "warning",
    icon: <WarningOutlined />,
    bgColor: "#fff7e6",
    borderColor: "#fa8c16",
    titleColor: "#d46b08",
    shadow: "0 4px 12px rgba(250, 140, 22, 0.18)"
  },
  KEY_PRESCRIBER: {
    label: "🚀 Prescripteur Clé (A+)",
    tagColor: "success",
    icon: <RocketOutlined />,
    bgColor: "#f6ffed",
    borderColor: "#52c41a",
    titleColor: "#237804",
    shadow: "0 4px 12px rgba(82, 196, 26, 0.18)"
  },
  STANDARD: {
    label: "👤 Standard",
    tagColor: "default",
    icon: <UserOutlined />,
    bgColor: "#f0f5ff",
    borderColor: "#adc6ff",
    titleColor: "#002766",
    shadow: "none"
  },
  REFRACTORY: {
    label: "⛔ Non Réceptif",
    tagColor: "error",
    icon: <StopOutlined />,
    bgColor: "#fff1f0",
    borderColor: "#ff4d4f",
    titleColor: "#cf1322",
    shadow: "0 4px 12px rgba(255, 77, 79, 0.18)"
  }
};

const getCyclePeriod = (weeks) => { 
    if (!weeks || weeks.length === 0) return {};
    const sortedWeeks = [...weeks].sort((a,b) => (a.week_number ?? 0) - (b.week_number ?? 0));
    return { 
        overallStartDate: sortedWeeks[0]?.start_date, 
        overallEndDate: sortedWeeks[sortedWeeks.length - 1]?.end_date 
    };
};

const PhysicianReturnIndexPage = () => {
    const dispatch = useDispatch();
    
    // --- ÉTATS DÉLÉGUÉS ---
    const [allDelegates, setAllDelegates] = useState([]);
    const [loadingDelegates, setLoadingDelegates] = useState(false);
    const [selectedDelegateIdForMain, setSelectedDelegateIdForMain] = useState(null);
    const [dateRange, setDateRange] = useState([dayjs('2026-01-01'), dayjs()]);
    const [selectedIsPartner, setSelectedIsPartner] = useState('all');

    // --- ÉTATS DONNÉES TABLEAU ---
    const [physicianReturnData, setPhysicianReturnData] = useState([]);
    const [loadingPhysicianReturnData, setLoadingPhysicianReturnData] = useState(false);
    const [errorPhysicianReturnData, setErrorPhysicianReturnData] = useState(null);
    
    // --- FILTRES LOCAUX TABLEAU ---
    const [selectedPhysicianFilterTable, setSelectedPhysicianFilterTable] = useState(null);
    const [selectedRegionFilterTable, setSelectedRegionFilterTable] = useState(null);
    const [selectedSpecialtyFilterTable, setSelectedSpecialtyFilterTable] = useState(null);

    // --- ÉTATS MODALE PERFORMANCE GLOBALE ---
    const [isDelegateModalVisible, setIsDelegateModalVisible] = useState(false);
    const [delegatePerformanceData, setDelegatePerformanceData] = useState([]);
    const [loadingDelegatePerformance, setLoadingDelegatePerformance] = useState(false);
    const [selectedDelegatesInModalFilter, setSelectedDelegatesInModalFilter] = useState([]);
    const [exporting, setExporting] = useState(false);
    const [exportingModal, setExportingModal] = useState(false); 

    // --- ÉTATS MODALE PARCOURS MÉDECIN ---
    const [doctorModalVisible, setDoctorModalVisible] = useState(false);
  const [selectedDoctorId, setSelectedDoctorId] = useState(null);

  const handleOpenDoctorHistory = (personId) => {
    setSelectedDoctorId(personId);
    setDoctorModalVisible(true);
  };

    // --- REDUX ---
    const delegateCycles = useSelector(state => state.planning.cycles);
    const loadingCycles = useSelector(state => state.planning.loading.cycles);
    const reduxDelegates = useSelector(state => state.delegates?.delegatesList?.data?.data || []);

    const allKnownDelegates = useMemo(() => {
        const map = new Map();
        [...(reduxDelegates || []), ...(allDelegates || [])].forEach(d => {
            if (d && d.id) {
                const prev = map.get(d.id);
                map.set(d.id, { ...prev, ...d, category: d.category || prev?.category });
            }
        });
        return Array.from(map.values());
    }, [reduxDelegates, allDelegates]);

    const getCategoryTagColor = (cat) => {
        if (!cat) return 'default';
        const c = String(cat).toUpperCase();
        if (c === 'AKTIV') return 'blue';
        if (c === 'SANOSAN') return 'green';
        if (c === 'MATERNITE') return 'purple';
        return 'cyan';
    };

    // Style de coloration de ligne identique à la page Rapports
    const getDoctorRowClass = useCallback((record) => {
        if (!record) return '';
        const cat = record.relationshipCategory || record.prescriptionProfile;
        const raw = String(cat || '').toUpperCase().trim();
        const isPart = record.isPartner === true || record.isPartner === 'true' || record.isPartner === 1 || raw === 'PARTNER_ACTIVE';

        if (raw === 'REFRACTORY' || raw.includes('RÉCEPTIF') || raw.includes('RECEPTIF')) {
            return 'row-refractory-red';
        }
        if (raw === 'PARTNER_AT_RISK' || raw.includes('VIGILANCE')) {
            return 'row-partner-warning';
        }
        if (raw === 'KEY_PRESCRIBER' || raw.includes('A+')) {
            return 'row-prescriber-green';
        }
        if (isPart) {
            return 'row-partner-gold';
        }
        return '';
    }, []);

    // Chargement initial des délégués
    useEffect(() => {
        setLoadingDelegates(true);
        getAllDelegatesList()
            .then(response => setAllDelegates(response?.data?.data || response?.data || []))
            .catch(() => message.error("Erreur chargement des délégués."))
            .finally(() => setLoadingDelegates(false));

        dispatch(getDelegatesList({ page: 1, itemsPerPage: 1000 }));
    }, [dispatch]);

    // Chargement des cycles quand un délégué est sélectionné
    useEffect(() => {
        if (selectedDelegateIdForMain) {
            dispatch(fetchCycles({ delegateId: selectedDelegateIdForMain }));
        }
    }, [dispatch, selectedDelegateIdForMain]);

    // =========================================================================
    // ⚡ OUVERTURE MODALE : RÉCUPÉRATION DU PARCOURS ET DU PROFIL COMPLET ⚡
    // =========================================================================

    // --- LOGIQUE ANALYSE DÉLÉGUÉ ---
    const handleFetchDataForMainTable = useCallback(async () => {
        if (!selectedDelegateIdForMain) {
            message.warning("Veuillez sélectionner un délégué pour l'analyse.");
            return;
        }
        setLoadingPhysicianReturnData(true); 
        setErrorPhysicianReturnData(null); 
        
        const queryParams = { _t: Date.now() };

        if (dateRange && dateRange[0] && dateRange[1]) {
            queryParams.startDate = dateRange[0].format('YYYY-MM-DD');
            queryParams.endDate = dateRange[1].format('YYYY-MM-DD');
        }

        if (selectedIsPartner && selectedIsPartner !== 'all') {
            queryParams.isPartner = selectedIsPartner;
        }

        try {
            const response = await getPhysicianReturnIndexForDelegate(selectedDelegateIdForMain, queryParams);
            
            const activePhysicians = (response.data || []).filter(item => 
                (item.timesTargetedInCycles > 0) || (item.effectiveVisitsReported > 0)
            );

            const dataWithKeys = activePhysicians.map(item => ({
                ...item,
                uniqueKey: `${selectedDelegateIdForMain}-${item.physicianId}`
            }));
            
            setPhysicianReturnData(dataWithKeys);
            setSelectedPhysicianFilterTable(null);
        } catch (err) {
            const errorMsg = err.response?.data?.message || err.message || "Erreur récupération des données.";
            message.error(errorMsg); 
            setErrorPhysicianReturnData(errorMsg);
            setPhysicianReturnData([]);
        } finally {
            setLoadingPhysicianReturnData(false);
        }
    }, [selectedDelegateIdForMain, dateRange, selectedIsPartner]);
    
    const individualDelegateKpis = useMemo(() => {
        if (!selectedDelegateIdForMain || physicianReturnData.length === 0 || loadingPhysicianReturnData) return null;
        let physiciansAboveTarget = 0; 
        let physiciansTargetedInCycles = 0;
        physicianReturnData.forEach(item => {
            if (item.timesTargetedInCycles > 0) {
                physiciansTargetedInCycles++;
                if (item.returnIndex !== null && item.returnIndex >= 66) {
                    physiciansAboveTarget++;
                }
            }
        });
        const successRatePercent = physiciansTargetedInCycles > 0 ? parseFloat(((physiciansAboveTarget / physiciansTargetedInCycles) * 100).toFixed(1)) : 0;
        return { 
            physiciansAboveTarget, 
            physiciansBelowTarget: physiciansTargetedInCycles - physiciansAboveTarget, 
            physiciansTargetedInCycles, 
            successRatePercent 
        };
    }, [physicianReturnData, selectedDelegateIdForMain, loadingPhysicianReturnData]);

    const fetchDelegatePerformanceData = useCallback(async (delegateIdsToFetch) => {
        if (!delegateIdsToFetch || delegateIdsToFetch.length === 0) return;
        setLoadingDelegatePerformance(true);
        const queryParams = {};
        if (dateRange && dateRange[0] && dateRange[1]) {
            queryParams.startDate = dateRange[0].format('YYYY-MM-DD');
            queryParams.endDate = dateRange[1].format('YYYY-MM-DD');
        }

        const performancePromises = delegateIdsToFetch.map(delegateId =>
            getPhysicianReturnIndexForDelegate(delegateId, queryParams)
                .then(response => {
                    const delegateInfo = allDelegates.find(d => d.id === delegateId);
                    const firstName = delegateInfo?.firstname || delegateInfo?.firstName || '';
                    const lastName = delegateInfo?.lastname || delegateInfo?.lastName || '';
                    const delegateNameStr = `${firstName} ${lastName}`.trim() || `ID: ${delegateId}`;

                    const data = response.data || [];
                    let targeted = 0; 
                    let above66 = 0;
                    data.forEach(phys => {
                        if (phys.timesTargetedInCycles > 0) {
                            targeted++;
                            if (phys.returnIndex >= 66) above66++;
                        }
                    });
                    return { 
                        key: delegateId, 
                        delegateId, 
                        delegateName: delegateNameStr, 
                        physiciansTargeted: targeted, 
                        physiciansAbove66: above66, 
                        performanceRate: targeted > 0 ? parseFloat(((above66 / targeted) * 100).toFixed(1)) : null 
                    };
                }).catch(() => null)
        );
        const results = await Promise.all(performancePromises);
        setDelegatePerformanceData(results.filter(r => r !== null));
        setLoadingDelegatePerformance(false);
    }, [dateRange, allDelegates]);

    const physicianOptions = useMemo(() => {
        return physicianReturnData
            .map(item => {
                const cat = item.relationshipCategory || (item.isPartner ? 'PARTNER_ACTIVE' : 'STANDARD');
                const emoji = 
                    cat === 'REFRACTORY' ? '⛔ ' :
                    cat === 'KEY_PRESCRIBER' ? '🚀 ' :
                    cat === 'PARTNER_AT_RISK' ? '⚠️ ' :
                    (item.isPartner ? '⭐ ' : '');

                return {
                    id: item.physicianId,
                    name: `${item.physicianFirstName || ''} ${item.physicianLastName || ''}`.trim() || `ID: ${item.physicianId}`,
                    label: `${emoji}${item.physicianFirstName || ''} ${item.physicianLastName || ''}`.trim(),
                    relationshipCategory: cat
                };
            })
            .sort((a, b) => a.name.localeCompare(b.name));
    }, [physicianReturnData]);

    const displayedPhysicianData = useMemo(() => {
        return physicianReturnData.filter(item => {
            const mPhysician = selectedPhysicianFilterTable ? item.physicianId === selectedPhysicianFilterTable : true;
            const mRegion = selectedRegionFilterTable ? item.regionName === selectedRegionFilterTable : true;
            const mSpecialty = selectedSpecialtyFilterTable ? item.specialtyName === selectedSpecialtyFilterTable : true;
            return mPhysician && mRegion && mSpecialty;
        });
    }, [physicianReturnData, selectedPhysicianFilterTable, selectedRegionFilterTable, selectedSpecialtyFilterTable]);

    const handleExport = async () => {
        if (!selectedDelegateIdForMain) return message.warning("Sélectionnez un délégué.");
        setExporting(true);
        try {
            const queryParams = dateRange ? { startDate: dateRange[0].format('YYYY-MM-DD'), endDate: dateRange[1].format('YYYY-MM-DD') } : {};
            if (selectedIsPartner !== 'all') queryParams.isPartner = selectedIsPartner;
            const response = await exportPhysicianReturnIndex(selectedDelegateIdForMain, queryParams);
            const url = window.URL.createObjectURL(new Blob([response.data]));
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', `indice_retour_${selectedDelegateIdForMain}_${dayjs().format('YYYY-MM-DD')}.csv`);
            document.body.appendChild(link);
            link.click();
            link.remove();
            message.success("Export réussi !");
        } catch { message.error("Erreur export."); }
        finally { setExporting(false); }
    };

    const handleExportModal = () => {
        const dataToExport = delegatePerformanceData.map(item => ({
            'Délégué': item.delegateName,
            'Médecins Ciblés': item.physiciansTargeted,
            'Indice ≥ 66%': item.physiciansAbove66,
            'Performance (%)': item.performanceRate ? `${item.performanceRate}%` : 'N/A'
        }));
        const csv = Papa.unparse(dataToExport, { delimiter: ';' });
        const blob = new Blob([`\uFEFF${csv}`], { type: 'text/csv;charset=utf-8;' });
        const link = document.createElement('a');
        link.href = URL.createObjectURL(blob);
        link.download = `performance_delegues_${dayjs().format('YYYY-MM-DD')}.csv`;
        link.click();
    };

    const columnsForMainTable = useMemo(() => returnIndexTableColumns(handleOpenDoctorHistory), []);
    const columnsForDelegatePerfModal = useMemo(() => delegatePerformanceTableColumns(), []);

    return (
        <div className="tabled">
            <Row gutter={[24, 24]}>
                {selectedDelegateIdForMain && !loadingPhysicianReturnData && individualDelegateKpis && (
                    <>
                        <Col xs={24} sm={12} md={12} lg={6} xl={6}>
                            <Card bordered={false} className="criclebox h-full">
                                <Statistic title="Médecins Indice ≥ 66%" value={individualDelegateKpis.physiciansAboveTarget} valueStyle={{ color: '#3f8600' }} prefix={<CheckCircleOutlined />} />
                            </Card>
                        </Col>
                        <Col xs={24} sm={12} md={12} lg={6} xl={6}>
                            <Card bordered={false} className="criclebox h-full">
                                <Statistic title="Ciblés sous l'indice" value={individualDelegateKpis.physiciansBelowTarget} valueStyle={{ color: '#cf1322' }} prefix={<IssuesCloseOutlined />} />
                            </Card>
                        </Col>
                        <Col xs={24} sm={12} md={12} lg={6} xl={6}>
                            <Card bordered={false} className="criclebox h-full">
                                <Statistic title="Total Médecins Ciblés" value={individualDelegateKpis.physiciansTargetedInCycles} />
                            </Card>
                        </Col>
                        <Col xs={24} sm={12} md={12} lg={6} xl={6}>
                            <Card bordered={false} className="criclebox h-full">
                                <Statistic
                                    title="Taux Médecins Performants"
                                    value={individualDelegateKpis.successRatePercent}
                                    suffix="%"
                                    precision={1}
                                    valueStyle={{ color: individualDelegateKpis.successRatePercent >= 66 ? '#3f8600' : '#cf1322' }}
                                />
                            </Card>
                        </Col>
                    </>
                )}

                <Col xs={24} xl={24}>
                    <Card
                        bordered={false}
                        className="criclebox tablespace mb-24 title_indice"
                        title={<Title level={4}><BarChartOutlined style={{marginRight: 8}} />Indice de Retour par Médecin</Title>}
                        extra={<Tooltip title="Calcul : (Visites Effectives / Cible Cycle) * 100"><InfoCircleOutlined style={{ color: '#1890ff' }}/></Tooltip>}
                    >
                        {/* FILTRES */}
                        <Row gutter={[16, 16]} style={{ marginBottom: 24, padding: '0 20px' }} align="bottom">
                            <Col xs={24} sm={12} md={5}>
                                <Text strong>Délégué :</Text>
                                <Select
                                    showSearch placeholder="Sélectionner un délégué"
                                    loading={loadingDelegates} value={selectedDelegateIdForMain}
                                    onChange={(v) => {
                                        setSelectedDelegateIdForMain(v);
                                        setSelectedPhysicianFilterTable(null);
                                        setPhysicianReturnData([]);
                                    }}
                                    style={{ width: '100%', marginTop: 5 }}
                                    filterOption={(input, option) => (option?.children ?? '').toLowerCase().includes(input.toLowerCase())}
                                >
                                     {allDelegates.map(d => <Option key={d.id} value={d.id}>{`${d.firstname || ''} ${d.lastname || ''}`.trim()}</Option>)}
                                </Select>
                            </Col>
                            <Col xs={24} sm={12} md={5}>
                                <Text strong>Période d'analyse :</Text>
                                <RangePicker value={dateRange} onChange={setDateRange} style={{ width: '100%', marginTop: 5 }} />
                            </Col>
                            <Col xs={24} sm={12} md={3}>
                                <Text strong>Partenaire :</Text>
                                <Select 
                                    value={selectedIsPartner} 
                                    onChange={setSelectedIsPartner} 
                                    style={{ width: '100%', marginTop: 5 }}
                                >
                                    <Option value="all">Tous</Option>
                                    <Option value="true">⭐ Partenaires</Option>
                                    <Option value="false">Standards</Option>
                                </Select>
                            </Col>

                            <Col xs={24} sm={12} md={5}>
                                <Text strong>Médecin :</Text>
                                <Select
                                    showSearch
                                    allowClear
                                    placeholder={physicianReturnData.length === 0 ? "Analyser d'abord" : "Tous les médecins"}
                                    value={selectedPhysicianFilterTable}
                                    onChange={(val) => setSelectedPhysicianFilterTable(val)}
                                    disabled={physicianReturnData.length === 0}
                                    style={{ width: '100%', marginTop: 5 }}
                                    filterOption={(input, option) => 
                                        (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
                                    }
                                    options={physicianOptions.map(p => ({
                                        value: p.id,
                                        label: `${p.isPartner ? '⭐ ' : ''}${p.name}`
                                    }))}
                                />
                            </Col>

                            <Col xs={24} md={6}>
                                <Space wrap style={{ width: '100%' }}>
                                    <Button type="primary" icon={<SearchOutlined />} onClick={handleFetchDataForMainTable} loading={loadingPhysicianReturnData}>Analyser</Button>
                                    <Button icon={<TeamOutlined />} onClick={() => setIsDelegateModalVisible(true)}>Comparer</Button>
                                    <Button icon={<DownloadOutlined />} onClick={handleExport} disabled={physicianReturnData.length === 0}>Exporter</Button>
                                </Space>
                            </Col>
                        </Row>

                        {/* CYCLES TAGS */}
                        {selectedDelegateIdForMain && (
                            <div style={{ padding: '0 20px 20px' }}>
                                <Text type="secondary" style={{ display: 'block', marginBottom: 10 }}><ScheduleOutlined /> Cycles actifs :</Text>
                                <Row gutter={[8, 8]}>
                                    {delegateCycles
                                        .filter(cycle => {
                                            const { overallStartDate } = getCyclePeriod(cycle.weeks);
                                            return overallStartDate && dayjs(overallStartDate).year() === 2026;
                                        })
                                        .map(cycle => {
                                            const { overallStartDate, overallEndDate } = getCyclePeriod(cycle.weeks);
                                            const period = (overallStartDate && overallEndDate)
                                                ? `${dayjs(overallStartDate).format('DD/MM/YY')} - ${dayjs(overallEndDate).format('DD/MM/YY')}`
                                                : null;

                                            return (
                                                <Col key={cycle.id}>
                                                    <Tag 
                                                        color="#0e519b" 
                                                        style={{ 
                                                            padding: '5px 10px', 
                                                            fontSize: '13px',
                                                            borderRadius: '4px',
                                                            display: 'flex',
                                                            flexDirection: 'column',
                                                            alignItems: 'flex-start',
                                                            lineHeight: '1.4'
                                                        }}
                                                    >
                                                        <div style={{ display: 'flex', justifyContent: 'space-between', width: '100%'}}>
                                                            <Text style={{ color: 'white', fontWeight: 600 }}>
                                                                {cycle.name || `Cycle ${cycle.id}`}
                                                            </Text>
                                                            <Text style={{ color: '#d9d9d9', marginLeft: 12, fontSize: '12px' }}>
                                                                ({cycle.persons?.length || 0})
                                                            </Text>
                                                        </div>
                                                        {period && (
                                                            <Text style={{ color: '#ffffff', fontSize: '11px', fontWeight: 600 }}>
                                                                {period}
                                                            </Text>
                                                        )}
                                                    </Tag>
                                                </Col>
                                            );
                                        })}
                                </Row>
                            </div>
                        )}

                        <Table
                            columns={columnsForMainTable}
                            dataSource={displayedPhysicianData}
                            rowKey="uniqueKey"
                            loading={loadingPhysicianReturnData}
                            rowClassName={(record) => getDoctorRowClass(record)}
                            pagination={{ pageSize: 50, showSizeChanger: true }}
                            scroll={{ x: 'max-content' }}
                        />
                    </Card>
                </Col>
            </Row>

            {/* MODALE PERFORMANCE DÉLÉGUÉS */}
            <AntdModal
                title={<Title level={5}><TeamOutlined /> Performance des Délégués</Title>}
                open={isDelegateModalVisible}
                onCancel={() => setIsDelegateModalVisible(false)}
                footer={[<Button key="close" onClick={() => setIsDelegateModalVisible(false)}>Fermer</Button>]}
                width="85%"
            >
                <Row gutter={[16,16]} style={{marginBottom: 20}}>
                    <Col span={18}>
                        <Select mode="multiple" placeholder="Filtrer Délégués" style={{ width: '100%' }} value={selectedDelegatesInModalFilter} onChange={setSelectedDelegatesInModalFilter}>
                            {allDelegates.map(d => <Option key={d.id} value={d.id}>{`${d.firstname} ${d.lastname}`}</Option>)}
                        </Select>
                    </Col>
                    <Col span={6}>
                        <Space>
                            <Button type="primary" onClick={() => fetchDelegatePerformanceData(selectedDelegatesInModalFilter.length > 0 ? selectedDelegatesInModalFilter : allDelegates.map(d => d.id))}>Calculer</Button>
                            <Button icon={<DownloadOutlined />} onClick={handleExportModal} disabled={delegatePerformanceData.length === 0}>Exporter</Button>
                        </Space>
                    </Col>
                </Row>
                <Table columns={columnsForDelegatePerfModal} dataSource={delegatePerformanceData} loading={loadingDelegatePerformance} rowKey="delegateId" />
            </AntdModal>

            {/* ========================================================================= */}
            {/* ⚡ MODALE PARCOURS ET PROFIL DU MÉDECIN (100% HOMOGÈNE À RAPPORTS) ⚡ */}
            {/* ========================================================================= */}
            <DoctorHistoryModal
        visible={doctorModalVisible}
        onClose={() => setDoctorModalVisible(false)}
        personId={selectedDoctorId}
      />
            <style>{`
                /* 1. Partenaire Stratégique (OR) */
                .row-partner-gold td {
                  background-color: #fffbe6 !important;
                }
                .row-partner-gold:hover td {
                  background-color: #fff1b8 !important;
                }
                .row-partner-gold a {
                  color: #856404 !important;
                  font-weight: 600 !important;
                }

                /* 2. Non Réceptif (ROUGE) */
                .row-refractory-red td {
                  background-color: #fff1f0 !important;
                }
                .row-refractory-red:hover td {
                  background-color: #ffccc7 !important;
                }
                .row-refractory-red a {
                  color: #cf1322 !important;
                  font-weight: bold !important;
                }

                /* 3. Partenaire Sous Vigilance (ORANGE) */
                .row-partner-warning td {
                  background-color: #fff7e6 !important;
                }
                .row-partner-warning:hover td {
                  background-color: #ffe7ba !important;
                }
                .row-partner-warning a {
                  color: #d46b08 !important;
                  font-weight: 600 !important;
                }

                /* 4. Prescripteur Clé A+ (VERT) */
                .row-prescriber-green td {
                  background-color: #f6ffed !important;
                }
                .row-prescriber-green:hover td {
                  background-color: #d9f7be !important;
                }
                .row-prescriber-green a {
                  color: #389e0d !important;
                  font-weight: 600 !important;
                }
            `}</style>
        </div>
    );
};

export default PhysicianReturnIndexPage;